import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { getScrollBehavior } from 'utils/motion';

import { TEAM_CHAT_MESSAGES_SCROLL_THRESHOLD_PX } from '../TeamChat.constants';
import {
  getMessageId,
  getMessageReplyToId,
  getReplyCountByParent,
  getTeamChatMessageDomId,
  isSystemMessage,
  parseTeamChatMessageHash,
} from '../TeamChat.utils';

import TeamChatMessageListItem from './components/TeamChatMessageListItem';
import {
  EMPTY_MEMBERS,
  EMPTY_PINNED_IDS,
  HIGHLIGHT_DURATION_MS,
} from './TeamChatMessageList.constants';
import { containerStyles, emptyStyles, loadMoreStyles } from './TeamChatMessageList.styles';
import { getMessageDateKey, isMessageGroupedWithPrevious } from './TeamChatMessageList.utils';

const TeamChatMessageList = memo(
  ({
    messages,
    chatId = null,
    currentUserId = null,
    members = EMPTY_MEMBERS,
    isLoading = false,
    hasMore = false,
    onLoadMore = null,
    pinnedIds = EMPTY_PINNED_IDS,
    onReplyTo = null,
    onTogglePin = null,
    initialUnreadMessageId = null,
  }) => {
    const containerRef = useRef(null);
    const lastMessageIdRef = useRef(null);
    const previousScrollHeightRef = useRef(0);
    const previousScrollTopRef = useRef(0);
    const latestMessageId = messages.length ? getMessageId(messages[messages.length - 1]) : null;

    const [highlightedMessageId, setHighlightedMessageId] = useState(null);

    const replyCountByParent = useMemo(() => getReplyCountByParent(messages), [messages]);
    const messagesById = useMemo(() => {
      const map = {};

      messages.forEach(message => {
        const id = getMessageId(message);

        if (id) {
          map[id] = message;
        }
      });

      return map;
    }, [messages]);

    const onScroll = useCallback(() => {
      const container = containerRef.current;

      if (!container || !hasMore || !onLoadMore || isLoading) {
        return;
      }

      if (container.scrollTop <= TEAM_CHAT_MESSAGES_SCROLL_THRESHOLD_PX) {
        previousScrollHeightRef.current = container.scrollHeight;
        previousScrollTopRef.current = container.scrollTop;
        onLoadMore();
      }
    }, [hasMore, isLoading, onLoadMore]);

    useLayoutEffect(() => {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      if (!latestMessageId || latestMessageId === lastMessageIdRef.current) {
        return;
      }

      if (_.isNull(lastMessageIdRef.current)) {
        container.scrollTop = container.scrollHeight;
        lastMessageIdRef.current = latestMessageId;

        return;
      }

      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        TEAM_CHAT_MESSAGES_SCROLL_THRESHOLD_PX;

      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
      }

      lastMessageIdRef.current = latestMessageId;
    }, [latestMessageId]);

    useEffect(() => {
      if (typeof window === 'undefined') {
        return _.noop;
      }

      const onHashChange = () => {
        const parsed = parseTeamChatMessageHash(window.location.hash);

        if (!parsed || parsed.chatId !== chatId) {
          return;
        }

        const element = document.getElementById(getTeamChatMessageDomId(parsed.messageId));

        if (element) {
          element.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });
          setHighlightedMessageId(parsed.messageId);
          window.setTimeout(() => setHighlightedMessageId(null), HIGHLIGHT_DURATION_MS);
        }
      };

      onHashChange();

      window.addEventListener('hashchange', onHashChange);

      return () => {
        window.removeEventListener('hashchange', onHashChange);
      };
    }, [chatId, messages]);

    useEffect(() => {
      const container = containerRef.current;

      if (!container || !previousScrollHeightRef.current) {
        return;
      }

      const delta = container.scrollHeight - previousScrollHeightRef.current;

      if (delta > 0) {
        container.scrollTop = previousScrollTopRef.current + delta;
      }

      previousScrollHeightRef.current = 0;
    }, [messages]);

    if (!messages.length && !isLoading) {
      return (
        <Box sx={emptyStyles}>
          <Typography variant="regular14">No messages yet</Typography>
        </Box>
      );
    }

    return (
      <Box ref={containerRef} sx={containerStyles} onScroll={onScroll}>
        {isLoading && hasMore ? (
          <Box sx={loadMoreStyles}>
            <CircularProgress size={20} />
          </Box>
        ) : null}
        {messages.map((message, index) => {
          const id = getMessageId(message);
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const currentDateKey = getMessageDateKey(message);
          const previousDateKey = previousMessage ? getMessageDateKey(previousMessage) : null;
          const shouldRenderDateDivider = currentDateKey && currentDateKey !== previousDateKey;
          const shouldRenderUnreadDivider =
            !!initialUnreadMessageId && id === initialUnreadMessageId;
          const replyToId = getMessageReplyToId(message);
          const replyParent = replyToId ? (messagesById[replyToId] ?? null) : null;
          const isGrouped =
            !shouldRenderDateDivider &&
            !shouldRenderUnreadDivider &&
            !replyParent &&
            !isSystemMessage(message) &&
            isMessageGroupedWithPrevious(message, previousMessage);

          return (
            <TeamChatMessageListItem
              chatId={chatId}
              currentDateKey={currentDateKey}
              currentUserId={currentUserId}
              isGrouped={isGrouped}
              isHighlighted={id === highlightedMessageId}
              isPinned={pinnedIds.includes(id)}
              key={id}
              members={members}
              message={message}
              replyCount={replyCountByParent[id] ?? 0}
              replyParent={replyParent}
              shouldRenderDateDivider={shouldRenderDateDivider}
              shouldRenderUnreadDivider={shouldRenderUnreadDivider}
              onReplyTo={onReplyTo}
              onTogglePin={onTogglePin}
            />
          );
        })}
      </Box>
    );
  },
);
TeamChatMessageList.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  chatId: PropTypes.string,
  currentUserId: PropTypes.string,
  initialUnreadMessageId: PropTypes.string,
  members: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  pinnedIds: PropTypes.arrayOf(PropTypes.string),
  onLoadMore: PropTypes.func,
  onReplyTo: PropTypes.func,
  onTogglePin: PropTypes.func,
};

export default TeamChatMessageList;
